import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Grid, Button, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const recruitersBenefits = [
    "Access to 50,000+ verified consultants",
    "Smart matching algorithm",
    "Direct messaging with candidates",
    "Real-time Candidate Submission updates",
    "Priority support",
];

const jobSeekersBenefits = [
    "Connect with top recruiters",
    "Showcase your skills & experience",
    "Get matched to relevant opportunities",
    "Track application status",
    "Create AI Powered Resume",
];

const Benefits = ({ activeLink }) => {
    const navigate = useNavigate();

    const recruiterCardStyle = {
        p: { xs: 4, md: 6 },
        backgroundColor: "#FFFFFF",
        borderRadius: "24px",
        height: "100%",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        border: activeLink === "For Recruiters" ? "2.5px solid #FF6B00" : "1px solid #E2E8F0",
        boxShadow: activeLink === "For Recruiters"
            ? "0 20px 40px rgba(255, 107, 0, 0.15), 0 0 20px rgba(255, 107, 0, 0.1)"
            : "0 4px 6px rgba(0,0,0,0.02)",
        transform: activeLink === "For Recruiters" ? "translateY(-8px)" : "none",
        "&:hover": {
            boxShadow: activeLink === "For Recruiters"
                ? "0 25px 50px rgba(255, 107, 0, 0.2)"
                : "0 20px 40px rgba(0,0,0,0.05)",
            transform: "translateY(-8px)",
        },
    };

    const jobseekerCardStyle = {
        p: { xs: 4, md: 6 },
        backgroundColor: "#FFFFFF",
        borderRadius: "24px",
        height: "100%",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        border: activeLink === "For Jobseekers" ? "2.5px solid #FF6B00" : "1px solid #E2E8F0",
        boxShadow: activeLink === "For Jobseekers"
            ? "0 20px 40px rgba(255, 107, 0, 0.15), 0 0 20px rgba(255, 107, 0, 0.1)"
            : "0 4px 6px rgba(0,0,0,0.02)",
        transform: activeLink === "For Jobseekers" ? "translateY(-8px)" : "none",
        "&:hover": {
            boxShadow: activeLink === "For Jobseekers"
                ? "0 25px 50px rgba(255, 107, 0, 0.2)"
                : "0 20px 40px rgba(0,0,0,0.05)",
            transform: "translateY(-8px)",
        },
    };

    return (
        <Box
            sx={{
                py: { xs: 1, md: 5 },
                backgroundColor: "#F3F5F780",
                fontFamily: "'Poppins', sans-serif",
            }}
        >
            <Container maxWidth="lg">
                {/* Header Section */}
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Box
                        sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.5,
                            borderRadius: "50px",
                            backgroundColor: "rgba(255, 107, 0, 0.1)",
                            mb: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#FF6B00",
                                fontWeight: 600,
                                fontSize: "14px",
                                fontFamily: "'Poppins', sans-serif",
                            }}
                        >
                            Benefits
                        </Typography>
                    </Box>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 700,
                            color: "#0B1121",
                            fontSize: { xs: "32px", md: "48px" },
                            mb: 2,
                            fontFamily: "'Poppins', sans-serif",
                        }}
                    >
                        Built for Everyone
                    </Typography>
                    <Typography
                        sx={{
                            color: "#64748B",
                            fontSize: { xs: "16px", md: "18px" },
                            maxWidth: "600px",
                            mx: "auto",
                            fontFamily: "'Poppins', sans-serif",
                        }}
                    >
                        Whether you're placing talent or seeking opportunities, we've got you covered.
                    </Typography>
                </Box>

                {/* Benefits Cards */}
                <Grid container spacing={4}>
                    {/* Recruiters Card */}
                    <Grid item xs={12} md={6} data-aos="fade-right" id="recruiters-section">
                        <Box sx={recruiterCardStyle}>
                            <Box
                                sx={{
                                    display: "inline-block",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: "50px",
                                    backgroundColor: "rgba(255, 107, 0, 0.1)",
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#FF6B00",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        fontFamily: "'Poppins', sans-serif",
                                    }}
                                >
                                    ● For Recruiters
                                </Typography>
                            </Box>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 500,
                                    color: "#0B1121",
                                    fontSize: { xs: "24px", md: "32px" },
                                    mb: 2,
                                    fontFamily: "'Poppins', sans-serif",
                                }}
                            >
                                Find the Perfect Consultant
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#64748B",
                                    fontSize: "16px",
                                    mb: 4,
                                    lineHeight: 1.6,
                                    fontFamily: "'Poppins', sans-serif",
                                }}
                            >
                                Stop wasting time on mismatched candidates. Our platform connects you with pre-qualified IT professionals ready to deploy.
                            </Typography>

                            <List sx={{ mb: 4 }}>
                                {recruitersBenefits.map((benefit, index) => (
                                    <ListItem key={index} disableGutters sx={{ py: 1 }}>
                                        <ListItemIcon sx={{ minWidth: "32px" }}>
                                            <CheckCircleIcon sx={{ color: "#FF6B00", fontSize: "20px" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography sx={{ color: "#334155", fontSize: "15px", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>
                                                    {benefit}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Button
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/login')}
                                sx={{
                                    background: "linear-gradient(45deg, #FF6B00 30%, #FF9100 90%)",
                                    color: "white",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    py: 1.5,
                                    px: 4,
                                    borderRadius: "8px",
                                    fontFamily: "'Poppins', sans-serif",
                                    boxShadow: "0 4px 14px 0 rgba(255, 107, 0, 0.39)",
                                    "&:hover": {
                                        background: "linear-gradient(45deg, #E65100 30%, #FF6B00 90%)",
                                        boxShadow: "0 6px 20px rgba(255, 107, 0, 0.23)",
                                    },
                                }}
                            >
                                Start Recruiting
                            </Button>
                        </Box>
                    </Grid>

                    {/* Job Seekers Card */}
                    <Grid item xs={12} md={6} data-aos="fade-left" id="jobseekers-section">
                        <Box sx={jobseekerCardStyle}>
                            <Box
                                sx={{
                                    display: "inline-block",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: "50px",
                                    backgroundColor: "rgba(30, 41, 59, 0.1)",
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#1E293B",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        fontFamily: "'Poppins', sans-serif",
                                    }}
                                >
                                    ● For Job Seekers
                                </Typography>
                            </Box>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 500,
                                    color: "#0B1121",
                                    fontSize: { xs: "24px", md: "32px" },
                                    mb: 2,
                                    fontFamily: "'Poppins', sans-serif",
                                }}
                            >
                                Land Your Dream Role
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#64748B",
                                    fontSize: "16px",
                                    mb: 4,
                                    lineHeight: 1.6,
                                    fontFamily: "'Poppins', sans-serif",
                                }}
                            >
                                Get discovered by top recruiters and staffing agencies. Showcase your skills and let opportunities find you.
                            </Typography>

                            <List sx={{ mb: 4 }}>
                                {jobSeekersBenefits.map((benefit, index) => (
                                    <ListItem key={index} disableGutters sx={{ py: 1 }}>
                                        <ListItemIcon sx={{ minWidth: "32px" }}>
                                            <CheckCircleIcon sx={{ color: "#1E293B", fontSize: "20px" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography sx={{ color: "#334155", fontSize: "15px", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>
                                                    {benefit}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Button
                                variant="outlined"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/login')}
                                sx={{
                                    borderColor: "#1E293B",
                                    color: "#1E293B",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    py: 1.5,
                                    px: 4,
                                    borderRadius: "8px",
                                    fontFamily: "'Poppins', sans-serif",
                                    borderWidth: "2px",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        borderWidth: "2px",
                                        borderColor: "#0F172A",
                                        background: "rgba(30, 41, 59, 0.05)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                Create Your Profile
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Benefits;
